import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

// Add auth token to requests
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface PatronCohort {
  id: string;
  creatorProfileId: string;
  cohortType: 'SIGNUP_MONTH' | 'TIER' | 'CAMPAIGN' | 'BEHAVIOR' | 'CUSTOM';
  startDate: Date;
  endDate?: Date;
  patronCount: number;
  averageLifetimeValue: number;
  retention30Day: number;
  retention60Day: number;
  retention90Day: number;
  churnRate: number;
  metadata?: any;
  lastCalculatedAt: Date;
  createdAt: Date;
  updatedAt: Date;
  creator?: {
    id: string;
    displayName: string;
  };
}

export interface CohortRetentionData {
  month0: number;
  month1: number;
  month2: number;
  month3: number;
  month4: number;
  month5: number;
  month6: number;
  month7: number;
  month8: number;
  month9: number;
  month10: number;
  month11: number;
  month12: number;
}

export interface CohortRevenueData {
  month: string;
  revenue: number;
  patronCount: number;
  avgRevenuePerPatron: number;
}

export interface CohortComparison {
  cohorts: PatronCohort[];
  retentionComparison: { [cohortId: string]: CohortRetentionData };
  revenueComparison: { [cohortId: string]: CohortRevenueData[] };
}

class CohortService {
  /**
   * Get all cohorts for a creator
   */
  async getCohorts(creatorProfileId?: string): Promise<PatronCohort[]> {
    const params = creatorProfileId ? { creatorProfileId } : {};
    const response = await axios.get(`${API_URL}/api/cohorts`, {
      params,
      headers: getAuthHeaders(),
    });
    return response.data.cohorts;
  }

  /**
   * Get a single cohort by ID
   */
  async getCohort(cohortId: string): Promise<PatronCohort> {
    const response = await axios.get(`${API_URL}/api/cohorts/${cohortId}`, {
      headers: getAuthHeaders(),
    });
    return response.data.cohort;
  }

  /**
   * Create a new cohort
   */
  async createCohort(data: {
    creatorProfileId: string;
    cohortType: 'SIGNUP_MONTH' | 'TIER' | 'CAMPAIGN' | 'BEHAVIOR' | 'CUSTOM';
    startDate: Date;
    endDate?: Date;
    metadata?: any;
  }): Promise<PatronCohort> {
    const response = await axios.post(
      `${API_URL}/api/cohorts`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data.cohort;
  }

  /**
   * Update cohort
   */
  async updateCohort(cohortId: string, data: Partial<PatronCohort>): Promise<PatronCohort> {
    const response = await axios.put(
      `${API_URL}/api/cohorts/${cohortId}`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data.cohort;
  }

  /**
   * Delete cohort
   */
  async deleteCohort(cohortId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/cohorts/${cohortId}`, {
      headers: getAuthHeaders(),
    });
  }

  /**
   * Recalculate cohort metrics
   */
  async recalculateCohort(cohortId: string): Promise<PatronCohort> {
    const response = await axios.post(
      `${API_URL}/api/cohorts/${cohortId}/recalculate`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data.cohort;
  }

  /**
   * Get cohort retention data
   */
  async getCohortRetention(cohortId: string): Promise<CohortRetentionData> {
    const response = await axios.get(`${API_URL}/api/cohorts/${cohortId}/retention`, {
      headers: getAuthHeaders(),
    });
    return response.data.retention;
  }

  /**
   * Get cohort revenue over time
   */
  async getCohortRevenue(cohortId: string): Promise<CohortRevenueData[]> {
    const response = await axios.get(`${API_URL}/api/cohorts/${cohortId}/revenue`, {
      headers: getAuthHeaders(),
    });
    return response.data.revenue;
  }

  /**
   * Compare multiple cohorts
   */
  async compareCohorts(cohortIds: string[]): Promise<CohortComparison> {
    const response = await axios.post(
      `${API_URL}/api/cohorts/compare`,
      { cohortIds },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Get patrons in a cohort
   */
  async getCohortPatrons(cohortId: string): Promise<any[]> {
    const response = await axios.get(`${API_URL}/api/cohorts/${cohortId}/patrons`, {
      headers: getAuthHeaders(),
    });
    return response.data.patrons;
  }

  /**
   * Export cohort data as CSV
   */
  async exportCohort(cohortId: string): Promise<Blob> {
    const response = await axios.get(`${API_URL}/api/cohorts/${cohortId}/export`, {
      headers: getAuthHeaders(),
      responseType: 'blob',
    });
    return response.data;
  }
}

export default new CohortService();
